// NextDoc renders PlantUML with a build-time server we cannot reach; mindmaps map 1:1 onto Mermaid mindmaps.

function cleanLabel(raw: string): string {
    return raw
        .replace(/<<[^<>]*>>/g, "")
        .replace(/<&[\w-]+>/g, "")
        .replace(/\[\[(\S+)(?:\s+([^\]]+))?\]\]/g, (_m, target: string, label?: string) => label ?? target)
        .replace(/<\/?[a-z][^>]*>/gi, "")
        .replace(/\*\*|""|__|~~|(?<!:)\/\//g, "")
        .replace(/^:/, "")
        .replace(/;$/, "")
        .replace(/\\n/g, " ")
        .replace(/\s+/g, " ")
        .trim();
}

function escapeLabel(label: string): string {
    return label.replace(/"/g, "#quot;");
}

/** Convert a PlantUML mindmap to a Mermaid mindmap, or return null for any other PlantUML diagram. */
export function plantumlMindmapToMermaid(source: string): string | null {
    if (!/@startmindmap/i.test(source)) return null;

    const nodes: { depth: number; label: string }[] = [];
    let inStyle = false;
    let multiline: { depth: number; parts: string[] } | null = null;

    for (const line of source.split(/\r?\n/)) {
        const trimmed = line.trim();
        if (multiline) {
            multiline.parts.push(trimmed);
            if (trimmed.endsWith(";")) {
                nodes.push({ depth: multiline.depth, label: cleanLabel(multiline.parts.join(" ")) });
                multiline = null;
            }
            continue;
        }
        if (/^<style>/i.test(trimmed)) inStyle = true;
        if (inStyle) {
            if (/<\/style>/i.test(trimmed)) inStyle = false;
            continue;
        }
        const match = line.match(/^(\s*)([*+-]+)_?\s*(.*)$/);
        if (!match) continue;
        // PlantUML also nests by indentation (" * child"), not only by the number of markers.
        const depth = match[1].replace(/\t/g, " ").length + match[2].length;
        const text = match[3].trim();
        if (text.startsWith(":") && !text.endsWith(";")) {
            multiline = { depth, parts: [text] };
            continue;
        }
        const label = cleanLabel(text);
        if (label) nodes.push({ depth, label });
    }

    if (nodes.length === 0) return null;

    const ancestors: number[] = [];
    const levels = nodes.map(node => {
        while (ancestors.length && ancestors[ancestors.length - 1] >= node.depth) ancestors.pop();
        ancestors.push(node.depth);
        return ancestors.length;
    });
    const offset = levels.filter(level => level === 1).length === 1 ? 0 : 1;
    const lines = ["mindmap"];
    if (offset) lines.push('  root[" "]');
    nodes.forEach((node, index) => {
        lines.push(`${"  ".repeat(levels[index] + offset)}n${index}["${escapeLabel(node.label)}"]`);
    });
    return lines.join("\n");
}
